'use client';

import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { getCsvFieldMappingWithRetry } from '@/services/workflow';
import {
  generateCsvMappingPrompt,
  generateCsvMappingSessionId,
  type CsvFieldMapping,
} from '@/utils/csvAiMapping';
import { useAnalytics } from '@/hooks/useAnalytics';
import { AUDIENCE_CSV_AI_MAPPING_USED } from '@/lib/analytics-events';
import appLogger from '@/utils/logger';

interface UseCsvAiMappingOptions {
  onReady?: (mapping: CsvFieldMapping) => void;
  onError?: () => void;
}

export function useCsvAiMapping(options: UseCsvAiMappingOptions = {}) {
  const { capture } = useAnalytics();
  const [aiMapping, setAiMapping] = useState<CsvFieldMapping | null>(null);
  const [editedMappings, setEditedMappings] = useState<Record<string, string>>({});

  const { mutate: analyzeWithAI, isPending: isAnalyzing } = useMutation({
    mutationFn: async ({ prompt, sessionId }: { prompt: string; sessionId: string; forcedType?: 'person' | 'company' }) => {
      return await getCsvFieldMappingWithRetry(prompt, sessionId);
    },
    onSuccess: (mapping, variables) => {
      const cleaned = Object.fromEntries(
        Object.entries(mapping.field_mappings).filter(([, v]) => v && v.trim() !== ''),
      );
      // When appending to an existing audience, hard-lock detected_type so a stray AI
      // guess can never route rows into the wrong table (the prompt already constrains it).
      const detected_type = variables.forcedType ?? mapping.detected_type;
      const cleanedMapping = { ...mapping, detected_type, field_mappings: cleaned };
      setAiMapping(cleanedMapping);
      setEditedMappings(cleaned);
      options.onReady?.(cleanedMapping);
    },
    onError: (err: unknown) => {
      appLogger.error('AI mapping failed:', { data: err });
      const fallback: CsvFieldMapping = {
        detected_type: 'person',
        confidence: 0,
        reasoning: 'Automatic analysis could not complete. Please map the fields manually.',
        field_mappings: {},
      };
      setAiMapping(fallback);
      setEditedMappings({});
      options.onError?.();
    },
  });

  const kickAnalysis = useCallback(
    (headers: string[], sampleRows: string[][], filename?: string, forcedType?: 'person' | 'company') => {
      try {
        const prompt = generateCsvMappingPrompt(headers, sampleRows, filename, forcedType);
        const sessionId = generateCsvMappingSessionId();
        capture(AUDIENCE_CSV_AI_MAPPING_USED, { field_count: headers.length });
        analyzeWithAI({ prompt, sessionId, forcedType });
      } catch (err) {
        appLogger.error('Failed to kick AI mapping:', { data: err });
      }
    },
    [analyzeWithAI, capture],
  );

  const resetMapping = useCallback(() => {
    setAiMapping(null);
    setEditedMappings({});
  }, []);

  return {
    aiMapping,
    setAiMapping,
    editedMappings,
    setEditedMappings,
    isAnalyzing,
    kickAnalysis,
    resetMapping,
  };
}
